import { useEffect, useState, useCallback, useRef } from "react";
import {
  fetchCoursePayments, fetchCoursePaymentsSummary,
  createCoursePayment, updateCoursePayment,
  fetchAllCourses, fetchBatches,
} from "./adminCourseService";
import { fetchAllUsersWithPagination } from "../services/adminUserService";
import "./adminCourses.css";

const fmt     = (paise) => paise != null ? `₹${(paise / 100).toLocaleString("en-IN")}` : "—";
const fmtDate = (iso)   => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const toLocalDT = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : "";

const PAY_BADGE = {
  SUCCESS: { cls: "ac-badge-green",  label: "Paid"          },
  PENDING: { cls: "ac-badge-orange", label: "Pending"       },
  FAILED:  { cls: "ac-badge-red",    label: "Failed"        },
  ADMIN:   { cls: "ac-badge-blue",   label: "Admin Granted" },
};
const ENROLL_BADGE = {
  ACTIVE:          "ac-badge-green",
  PENDING_PAYMENT: "ac-badge-orange",
  CANCELLED:       "ac-badge-red",
  COMPLETED:       "ac-badge-blue",
};
const ENROLL_LABEL = {
  ACTIVE: "Active", PENDING_PAYMENT: "Pending Payment",
  CANCELLED: "Cancelled", COMPLETED: "Completed",
};

/* ── Toast ── */
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  const bg = type === "success" ? "#16a34a" : "#dc2626";
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, background: bg, color: "#fff",
      padding: "12px 20px", borderRadius: 8, zIndex: 9999,
      boxShadow: "0 4px 16px rgba(0,0,0,0.18)", fontSize: "0.9rem", maxWidth: 360,
    }}>
      {msg}
    </div>
  );
};

/* ── Searchable user picker ── */
const UserPicker = ({ value, onChange }) => {
  const [q, setQ]           = useState("");
  const [results, setRes]   = useState([]);
  const [searching, setSrch] = useState(false);
  const debRef = useRef(null);

  const search = (val) => {
    setQ(val);
    onChange(null);
    clearTimeout(debRef.current);
    if (!val.trim()) { setRes([]); return; }
    debRef.current = setTimeout(async () => {
      setSrch(true);
      try {
        const r = await fetchAllUsersWithPagination({ search: val, page: 0, size: 8 });
        setRes(Array.isArray(r.data?.data) ? r.data.data : []);
      } catch { setRes([]); }
      finally { setSrch(false); }
    }, 350);
  };

  const display = value ? `${value.name} (${value.email})` : q;

  return (
    <div style={{ position: "relative" }}>
      <input
        value={display}
        onChange={(e) => search(e.target.value)}
        onFocus={() => { if (value) { onChange(null); setQ(""); setRes([]); } }}
        placeholder="Search by name or email…"
      />
      {(results.length > 0 || searching) && !value && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
          zIndex: 100, maxHeight: 200, overflowY: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {searching && <div style={{ padding: "0.5rem 0.9rem", fontSize: "0.82rem", color: "#9ca3af" }}>Searching…</div>}
          {results.map((u) => (
            <div key={u.id}
              onClick={() => { onChange(u); setQ(""); setRes([]); }}
              style={{ padding: "0.5rem 0.9rem", cursor: "pointer", fontSize: "0.88rem", borderBottom: "1px solid #f3f4f6" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f0f9ff"}
              onMouseLeave={(e) => e.currentTarget.style.background = ""}
            >
              <strong>{u.name}</strong> <span style={{ color: "#6b7280" }}>{u.email}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Batch picker (course → batch) ── */
const BatchPicker = ({ value, onChange }) => {
  const [courses, setCourses]   = useState([]);
  const [batches, setBatches]   = useState([]);
  const [courseId, setCourseId] = useState("");

  useEffect(() => {
    fetchAllCourses().then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);

  const handleCourse = async (cid) => {
    setCourseId(cid);
    onChange(null);
    setBatches([]);
    if (!cid) return;
    try {
      const r = await fetchBatches(cid);
      setBatches(r.data || []);
    } catch { setBatches([]); }
  };

  return (
    <div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
      <select value={courseId} onChange={(e) => handleCourse(e.target.value)}>
        <option value="">— Select course —</option>
        {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {courseId && (
        <select value={value?.id || ""} onChange={(e) => {
          const b = batches.find((x) => String(x.id) === e.target.value);
          onChange(b || null);
        }}>
          <option value="">— Select batch —</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>{b.batchName}</option>
          ))}
        </select>
      )}
    </div>
  );
};

/* ── Add Transaction Modal ── */
const AddModal = ({ onClose, onSuccess, onSwitchToEdit }) => {
  const [user,   setUser]   = useState(null);
  const [batch,  setBatch]  = useState(null);
  const [amount, setAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const [payId,   setPayId]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");

  const handleSubmit = async () => {
    if (!user || !batch || !amount) { setErr("User, batch and amount are required."); return; }
    setSaving(true); setErr("");
    try {
      const res = await createCoursePayment({
        userId:             user.id,
        batchId:            batch.id,
        amountPaid:         Math.round(parseFloat(amount) * 100),
        razorpayOrderId:    orderId || undefined,
        razorpayPaymentId:  payId   || undefined,
      });
      onSuccess(res.data);
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create transaction.";
      const match = msg.match(/enrollmentId=(\d+)/);
      if (match) {
        onSwitchToEdit(parseInt(match[1], 10), msg);
      } else {
        setErr(msg);
      }
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} title="Add Transaction">
      <div className="ac-form-group"><label>User *</label><UserPicker value={user} onChange={setUser} /></div>
      <div className="ac-form-group"><label>Batch *</label><BatchPicker value={batch} onChange={setBatch} /></div>
      <div className="ac-form-group">
        <label>Amount (₹) *</label>
        <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 2999" />
      </div>
      <div className="ac-form-group"><label>Razorpay Order ID</label><input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="order_xxx (optional)" /></div>
      <div className="ac-form-group"><label>Razorpay Payment ID</label><input value={payId} onChange={(e) => setPayId(e.target.value)} placeholder="pay_yyy (optional)" /></div>
      {err && <div style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="ac-actions">
        <button className="ac-btn ac-btn-primary" disabled={saving} onClick={handleSubmit}>{saving ? "Saving…" : "Add Transaction"}</button>
        <button className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Overlay>
  );
};

/* ── Edit Transaction Modal ── */
const EditModal = ({ row, onClose, onSuccess }) => {
  const [amount,   setAmount]   = useState(row.amount != null ? String(row.amount / 100) : "");
  const [orderId,  setOrderId]  = useState(row.razorpayOrderId  || "");
  const [payId,    setPayId]    = useState(row.razorpayPaymentId || "");
  const [status,   setStatus]   = useState(row.enrollmentStatus || "");
  const [enrolledAt, setEnrolledAt] = useState(toLocalDT(row.enrolledAt));
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState("");

  const handleSubmit = async () => {
    setSaving(true); setErr("");
    const body = {};
    if (amount    !== String(row.amount / 100))       body.amountPaid        = Math.round(parseFloat(amount) * 100);
    if (orderId   !== (row.razorpayOrderId  || ""))   body.razorpayOrderId   = orderId;
    if (payId     !== (row.razorpayPaymentId || ""))  body.razorpayPaymentId = payId;
    if (status    !== row.enrollmentStatus)           body.status            = status;
    if (enrolledAt && enrolledAt !== toLocalDT(row.enrolledAt))
      body.enrolledAt = new Date(enrolledAt).toISOString();

    if (!Object.keys(body).length) { onClose(); return; }
    try {
      const res = await updateCoursePayment(row.enrollmentId, body);
      onSuccess(res.data);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to update.");
    } finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} title={`Edit Transaction #${row.enrollmentId}`}>
      <div style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "1rem" }}>
        {row.userName} — {row.courseName} / {row.batchName}
      </div>
      <div className="ac-form-group">
        <label>Amount (₹)</label>
        <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="ac-form-group"><label>Razorpay Order ID</label><input value={orderId} onChange={(e) => setOrderId(e.target.value)} /></div>
      <div className="ac-form-group"><label>Razorpay Payment ID</label><input value={payId} onChange={(e) => setPayId(e.target.value)} /></div>
      <div className="ac-form-group">
        <label>Enrollment Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ACTIVE">Active</option>
          <option value="PENDING_PAYMENT">Pending Payment</option>
          <option value="CANCELLED">Cancelled</option>
          <option value="COMPLETED">Completed</option>
        </select>
      </div>
      <div className="ac-form-group">
        <label>Enrolled At</label>
        <input type="datetime-local" value={enrolledAt} onChange={(e) => setEnrolledAt(e.target.value)} />
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="ac-actions">
        <button className="ac-btn ac-btn-primary" disabled={saving} onClick={handleSubmit}>{saving ? "Saving…" : "Save Changes"}</button>
        <button className="ac-btn ac-btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Overlay>
  );
};

/* ── Overlay wrapper ── */
const Overlay = ({ title, children, onClose }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  }}>
    <div style={{
      background: "#fff", borderRadius: 10, padding: "1.5rem",
      width: "100%", maxWidth: 440, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      maxHeight: "90vh", overflowY: "auto",
    }}>
      <h3 style={{ margin: "0 0 1.25rem", fontSize: "1.1rem" }}>{title}</h3>
      {children}
    </div>
  </div>
);

/* ── Summary card ── */
const SummaryCard = ({ label, value, sub }) => (
  <div style={{
    background: "#fff", borderRadius: 10, padding: "1rem 1.25rem",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", minWidth: 140,
  }}>
    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#0f172a" }}>{value}</div>
    {sub && <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 2 }}>{sub}</div>}
  </div>
);

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const AdminCoursePaymentsPage = () => {
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });

  const [filters, setFilters] = useState({ search: "", status: "", courseId: "", batchId: "", startDate: "", endDate: "" });
  const [applied, setApplied] = useState(filters);

  const [showAdd,  setShowAdd]  = useState(false);
  const [editRow,  setEditRow]  = useState(null);   // row object for edit modal
  const [toast,    setToast]    = useState(null);   // { msg, type }

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const loadSummary = useCallback(() => {
    fetchCoursePaymentsSummary().then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async (page = 0) => {
    setLoading(true); setError("");
    try {
      const params = { page, size: pagination.size };
      if (applied.search)    params.search    = applied.search;
      if (applied.status)    params.status    = applied.status;
      if (applied.courseId)  params.courseId  = applied.courseId;
      if (applied.batchId)   params.batchId   = applied.batchId;
      if (applied.startDate) params.startDate = new Date(applied.startDate).toISOString();
      if (applied.endDate)   params.endDate   = new Date(applied.endDate).toISOString();
      const res = await fetchCoursePayments(params);
      const d = res.data;
      setRows(d.content || []);
      setPagination((p) => ({ ...p, page: d.number, totalPages: d.totalPages, totalElements: d.totalElements }));
    } catch { setError("Failed to load payments."); }
    finally  { setLoading(false); }
  }, [applied, pagination.size]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(0); }, [applied]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => setApplied({ ...filters });
  const handleReset = () => {
    const e = { search: "", status: "", courseId: "", batchId: "", startDate: "", endDate: "" };
    setFilters(e); setApplied(e);
  };

  const handleSuccess = (msg) => {
    setShowAdd(false); setEditRow(null);
    showToast(msg);
    load(pagination.page);
    loadSummary();
  };

  // called from AddModal when 400 with enrollmentId= in message
  const handleSwitchToEdit = (enrollmentId, errMsg) => {
    setShowAdd(false);
    const existing = rows.find((r) => r.enrollmentId === enrollmentId);
    if (existing) {
      setEditRow(existing);
    } else {
      showToast(errMsg, "error");
    }
  };

  return (
    <div>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="ac-header">
        <h2>Course Payments</h2>
        <button className="ac-btn ac-btn-primary" onClick={() => setShowAdd(true)}>+ Add Transaction</button>
      </div>

      {/* summary */}
      {summary && (
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <SummaryCard label="Total Revenue"     value={fmt(summary.totalRevenue)} />
          <SummaryCard label="Total Enrollments" value={summary.totalEnrollments} />
          <SummaryCard label="Successful"        value={summary.successfulPayments} />
          <SummaryCard label="Pending"           value={summary.pendingPayments} />
          <SummaryCard label="Cancelled"         value={summary.cancelledPayments} />
          <SummaryCard label="Admin Granted"     value={summary.adminGrantedCount} sub="no payment" />
        </div>
      )}

      {/* filters */}
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1rem 1.25rem",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: "1.25rem",
        display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div className="ac-form-group" style={{ margin: 0, minWidth: 180 }}>
          <label>Search</label>
          <input placeholder="Name or email…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>Payment Status</label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            <option value="SUCCESS">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
            <option value="ADMIN">Admin Granted</option>
          </select>
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>Course ID</label>
          <input type="number" placeholder="e.g. 2" value={filters.courseId} onChange={(e) => setFilters((f) => ({ ...f, courseId: e.target.value }))} style={{ width: 90 }} />
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>Batch ID</label>
          <input type="number" placeholder="e.g. 5" value={filters.batchId} onChange={(e) => setFilters((f) => ({ ...f, batchId: e.target.value }))} style={{ width: 90 }} />
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>From</label>
          <input type="date" value={filters.startDate} onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))} />
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>To</label>
          <input type="date" value={filters.endDate} onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))} />
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="ac-btn ac-btn-primary" onClick={handleApply}>Apply</button>
          <button className="ac-btn ac-btn-ghost"   onClick={handleReset}>Reset</button>
        </div>
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.6rem 0.9rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.88rem" }}>{error}</div>}

      {/* table */}
      {loading ? (
        <div className="ac-loading">Loading payments…</div>
      ) : rows.length === 0 ? (
        <div className="ac-empty">No payments found.</div>
      ) : (
        <>
          <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: "0.5rem" }}>
            {pagination.totalElements} record{pagination.totalElements !== 1 ? "s" : ""}
          </div>
          <div className="ac-table-wrap">
            <table className="ac-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Course / Batch</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Enrollment</th>
                  <th>Order ID</th>
                  <th>Enrolled At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const ps = PAY_BADGE[r.paymentStatus] || { cls: "ac-badge-gray", label: r.paymentStatus };
                  return (
                    <tr key={r.enrollmentId}>
                      <td style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{r.enrollmentId}</td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{r.userName}</div>
                        <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.userEmail}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.courseName}</div>
                        <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.batchName}</div>
                      </td>
                      <td style={{ fontWeight: 700, color: "#028891" }}>{fmt(r.amount)}</td>
                      <td>
                        <span className={`ac-badge ${ps.cls}`}>{ps.label}</span>
                        {r.adminGranted && <span className="ac-badge ac-badge-blue" style={{ marginLeft: 4, fontSize: "0.68rem" }}>Admin</span>}
                      </td>
                      <td>
                        <span className={`ac-badge ${ENROLL_BADGE[r.enrollmentStatus] || "ac-badge-gray"}`}>
                          {ENROLL_LABEL[r.enrollmentStatus] || r.enrollmentStatus}
                        </span>
                      </td>
                      <td style={{ fontSize: "0.78rem", color: "#6b7280", fontFamily: "monospace" }}>{r.razorpayOrderId || "—"}</td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7280", whiteSpace: "nowrap" }}>{fmtDate(r.enrolledAt)}</td>
                      <td>
                        <button
                          className="ac-btn ac-btn-ghost ac-btn-sm"
                          onClick={() => setEditRow(r)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1rem", flexWrap: "wrap" }}>
              <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={pagination.page === 0} onClick={() => load(pagination.page - 1)}>← Prev</button>
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <button key={i} className={`ac-btn ac-btn-sm ${pagination.page === i ? "ac-btn-primary" : "ac-btn-ghost"}`} onClick={() => load(i)}>{i + 1}</button>
              ))}
              <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={pagination.page === pagination.totalPages - 1} onClick={() => load(pagination.page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => handleSuccess("Transaction added successfully.")}
          onSwitchToEdit={handleSwitchToEdit}
        />
      )}

      {editRow && (
        <EditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSuccess={() => handleSuccess("Transaction updated successfully.")}
        />
      )}
    </div>
  );
};

export default AdminCoursePaymentsPage;
